import { useEffect } from 'react'
import { Collapse, Box, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import attributes from './attributes'
import { AppLayout, useEidGrid, CardViewLoader } from 'components'
import { useIsSmallScreen, useQuery, useSessionStorage } from 'packages/core'
import { useHistory } from 'react-router'
import StickyContainer from 'components/StickyContainer'
import { CollapsibleFilterPanel } from 'packages/eid-ui'
import {
    Filters,
    ListingGrid,
    GridContainer,
    FiltersColumn,
    FilterContainer,
    DesktopContainer,
    MobileFiltersScreen,
    SmallScreenContainer,
    MobileStickyContentListing,
} from 'components'
import { SubPathLevel } from 'appConfigurations'

import {
    ExpandableTable,
    ExpandableTableHead,
    ExpandableTableRow,
} from 'components/ExpandableTable'
import { ExpandedContent } from './ExpandedContent'

const StyledFilterSearch = styled(Box)({
    width: '100%',
    maxWidth: '40rem',
    '@media (max-width:1700px)': {
        maxWidth: '30rem',
    },
})

const StyledDateFilterSearchCollapse = styled(Box)({
    width: '100%',
    padding: '0rem 0.5rem',
    height: '4rem',
    maxWidth: '100%',
})

const StyledFilterMainSeach = styled(Box)({
    display: 'flex',
    width: '100%',
    position: 'relative',
    zIndex: 0,
})

const BusinessRequestItemsLayout = ({
    list,
    totalCount,
    isLoading,
    isLoadingMore,
    pagination,
    filters = [],
}) => {
    const { t } = useTranslation()
    const [openFilters, setOpenFilters] = useSessionStorage(
        'FILTERS_EXPANDED',
        true,
    )
    const isSmallScreen = useIsSmallScreen()
    const defaultView = isSmallScreen ? 'card' : 'table'
    const { viewSwitcher, setCurrentView, viewToRender } = useEidGrid({
        isLoading,
        data: list,
        isLoadingMore,
        noDataMessage: t('NoBusinessRequestItemsFound'),
        pagination,
        attributes,
        // Card Component will be added later after UX design
        // cardViewConfig: {
        //     LoadingIndicatorComponent: CardViewLoader,
        //     CardComponent: ReportCard,
        // },
        tableViewConfig: {
            TableComponent: ExpandableTable,
            TableHeadComponent: ExpandableTableHead,
            TableRowComponent: ExpandableTableRow,
            expandableComponent: (data) => <ExpandedContent data={data} />,
            isRowClickable: false,
        },
        defaultView,
    })

    useEffect(() => {
        if (isSmallScreen) {
            setCurrentView('card')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSmallScreen])

    const query = useQuery()
    const id = query.get('id')
    const history = useHistory()

    const {
        location: { pathname },
    } = history
    const areaName = pathname.split('/')[1 + SubPathLevel]
    const routeName = pathname.split('/')[2 + SubPathLevel]

    const showFilters = query.get('filters') === 'visible'
    const dateFilterOptions = [
        {
            label: t('RequestApproved'),
            enumValue: 'StatusChanged',
            value: 'requestApproved',
            default: true,
        },
    ]

    const mobileFiltersScreen = (
        <MobileFiltersScreen
            totalCount={totalCount}
            filters={filters}
            handleApplyFilters={() => {
                query.delete('filters')
                history.push(`${history.location.pathname}?${query.toString()}`)
            }}
        />
    )

    const mobileStickyContentListing = (
        <MobileStickyContentListing
            totalCount={totalCount}
            title={areaName}
            subTitle={routeName}
            handleApplyFilters={() => {
                query.set('filters', 'visible')
                history.push(`${history.location.pathname}?${query.toString()}`)
            }}
        />
    )

    let content
    if (isSmallScreen && false) {
        //TODO: remove false when the cards are implemented
        content = (
            <SmallScreenContainer>
                <Box display={showFilters ? '' : 'none'}>
                    {mobileFiltersScreen}
                </Box>

                <Box display={!showFilters ? '' : 'none'}>
                    <Box display={!id ? '' : 'none'}>
                        <StickyContainer>
                            <Box width="100%" padding="1.2rem 1.4rem">
                                {mobileStickyContentListing}
                            </Box>
                        </StickyContainer>
                        {viewToRender}
                    </Box>
                </Box>
            </SmallScreenContainer>
        )
    } else {
        content = (
            <DesktopContainer>
                <FiltersColumn>
                    <CollapsibleFilterPanel
                        openFilters={openFilters}
                        setOpenFilters={(e) => setOpenFilters((prev) => !prev)}
                    />
                    <Collapse in={openFilters} timeout={300} unmountOnExit>
                        <>
                            {filters.map((f) => {
                                const FilterToRender =
                                    Filters.filtersMap[f.name]
                                return (
                                    <FilterContainer
                                        data-protectedsubcomponent={
                                            f.requireAccess
                                        }
                                        key={f.name}
                                    >
                                        <FilterToRender />
                                    </FilterContainer>
                                )
                            })}
                        </>
                    </Collapse>
                </FiltersColumn>

                <Box width="100%">
                    <StyledFilterMainSeach>
                        <StyledFilterSearch>
                            <Filters.TextSearch />
                        </StyledFilterSearch>
                        <StyledDateFilterSearchCollapse>
                            <Filters.DateFilter options={dateFilterOptions} />
                        </StyledDateFilterSearchCollapse>
                    </StyledFilterMainSeach>

                    <ListingGrid>
                        <GridContainer openFilters={openFilters}>
                            {viewToRender}
                        </GridContainer>
                    </ListingGrid>
                </Box>
            </DesktopContainer>
        )
    }

    return <AppLayout totalCount={totalCount}>{content}</AppLayout> // TODO: Add viewSwitcher={viewSwitcher} when cards are ready
}

export default BusinessRequestItemsLayout
